import { emitToast, emitDialog } from '../events/error/errorEventBus';
import { ERROR_CATALOG, ErrorCode } from './catalog';
import type { AppError, ErrorContext } from './types';
import { normalizeError } from './normalizer';
import { t } from '../constants/i18n';
import { useCartStore } from '../store/useCartStore';

type ResolvedPolicy = {
  userMessageKey: string;
  level: 'info' | 'warning' | 'error';
  strategy: 'TOAST' | 'MODAL' | 'SILENT' | 'INLINE';
  dedupeWindowMs?: number;
};

function resolvePolicy(error: AppError, ctx: ErrorContext): ResolvedPolicy {
  const policy = ERROR_CATALOG[error.code] ?? ERROR_CATALOG.UNKNOWN;
  const scopePolicy = policy.scopes?.[ctx.scope] ?? {};

  return {
    userMessageKey: scopePolicy.userMessageKey ?? policy.default.userMessageKey,
    level: scopePolicy.level ?? policy.default.level ?? error.level,
    strategy: scopePolicy.strategy ?? policy.default.strategy,
    dedupeWindowMs: scopePolicy.dedupeWindowMs ?? policy.default.dedupeWindowMs
  };
}

function resolveMessage(error: AppError, policy: ResolvedPolicy): string {
  if (policy.userMessageKey === 'stockLimitExceeded') {
    const maxQty = typeof error.context?.maxQty === 'number' ? error.context.maxQty : 999;
    return t('stockLimitExceeded', maxQty);
  }

  return t(policy.userMessageKey as Parameters<typeof t>[0]);
}

function getDedupeKey(error: AppError, ctx: ErrorContext) {
  const context = error.context;
  const identity = [
    error.code,
    context?.itemKey,
    context?.addKey,
    context?.discountCode,
    context?.endpoint,
    ctx.scope,
    ctx.correlationId
  ]
    .filter(Boolean)
    .join(':');

  return identity || error.code;
}

const dedupeCache = new Map<string, number>();
const deferredErrors = new Map<string, { error: AppError; ctx: ErrorContext }>();
let deferredTimer: number | null = null;

function isInteractingNow(): boolean {
  if (typeof window === 'undefined') return false;
  return useCartStore.getState().isUserInteracting();
}

function shouldDefer(error: AppError, ctx: ErrorContext, policy: ResolvedPolicy): boolean {
  if (ctx.silent || ctx.handledLocally) return false;
  if (policy.strategy === 'SILENT' || policy.strategy === 'INLINE') return false;
  if (ctx.scope !== 'cartDrawer') return false;
  if (error.code === ErrorCode.CART_SYNC_FAILED) return false;
  return isInteractingNow();
}

function scheduleDeferredFlush() {
  if (deferredTimer || typeof window === 'undefined') return;
  deferredTimer = window.setTimeout(() => {
    deferredTimer = null;
    flushDeferred();
  }, 120);
}

function flushDeferred() {
  if (isInteractingNow()) {
    scheduleDeferredFlush();
    return;
  }
  const queued = Array.from(deferredErrors.values());
  deferredErrors.clear();
  queued.forEach(({ error, ctx }) => {
    processResolved(error, ctx, false);
  });
}

function shouldDedupe(key: string, windowMs: number) {
  const now = Date.now();
  const last = dedupeCache.get(key);
  if (last && now - last < windowMs) return true;
  dedupeCache.set(key, now);
  return false;
}

function dispatch(error: AppError, policy: ResolvedPolicy, message: string) {
  if (policy.strategy === 'TOAST') {
    emitToast({
      type: 'error',
      message,
      code: error.code,
      level: policy.level,
      messageKey: policy.userMessageKey,
      context: error.context
    });
    return;
  }

  if (policy.strategy === 'MODAL' && error.code === ErrorCode.CART_STOCK_LIMIT) {
    emitDialog({
      type: 'stock-limit',
      code: error.code,
      level: policy.level,
      messageKey: policy.userMessageKey,
      context: error.context,
      maxQty: typeof error.context?.maxQty === 'number' ? error.context.maxQty : 999,
      requestedQty:
        typeof error.context?.requestedQty === 'number' ? error.context.requestedQty : undefined
    });
  }
}

function report(error: AppError, ctx: ErrorContext) {
  const detail = {
    error,
    context: ctx,
    timestamp: Date.now()
  };

  if (typeof window !== 'undefined') {
    const existing = Array.isArray((window as any).__DEBUG_CART_ERRORS__)
      ? (window as any).__DEBUG_CART_ERRORS__
      : [];
    const next = [...existing, detail];
    const maxEntries = 100;
    (window as any).__DEBUG_CART_ERRORS__ = next.length > maxEntries
      ? next.slice(next.length - maxEntries)
      : next;
  }

  if (import.meta.env.MODE !== 'production') {
    console.error('[cart][errorHandler]', detail);
  }
}

export class GlobalErrorHandler {
  static handle(error: unknown, ctx: ErrorContext) {
    const normalized = normalizeError(error);
    processResolved(normalized, ctx, true);
  }
}

function processResolved(error: AppError, ctx: ErrorContext, allowDefer: boolean) {
  const policy = resolvePolicy(error, ctx);
  const message = resolveMessage(error, policy);
  const dedupeKey = getDedupeKey(error, ctx);

  if (allowDefer && shouldDefer(error, ctx, policy)) {
    deferredErrors.set(dedupeKey, { error, ctx });
    scheduleDeferredFlush();
    return;
  }

  if (ctx.silent || ctx.handledLocally || policy.strategy === 'SILENT') {
    report(error, ctx);
    return;
  }

  if (policy.dedupeWindowMs && shouldDedupe(dedupeKey, policy.dedupeWindowMs)) {
    report(error, ctx);
    return;
  }

  if (policy.strategy === 'INLINE') {
    report(error, ctx);
    return;
  }

  dispatch(error, policy, message);
  report(error, ctx);
}
